<template>
  <div class="sign-in">
    <h2 class="title" :class="{ 'register-auto-login': actionType === ActionType.RegisterAutoLogin }">
      {{ pageTitle }}
    </h2>
    <el-form ref="loginForm" :model="form" size="large" label-position="top" @submit.prevent>
      <el-form-item :label="$t('message.signIn.email')" prop="email" :rules="emailRules">
        <el-autocomplete
          ref="email"
          v-model="form.email"
          :fetch-suggestions="emailSuggestions"
          clearable
          class="email-input"
          :class="{ 'email-confirmed': passwordVisible }"
          :placeholder="$t('message.signIn.emailPlaceholder')"
          :disabled="loading || actionType === ActionType.RegisterAutoLogin"
          @keyup.enter="doNext"
          @change="onEmailChange"
          @input="onEmailChange"
        />
      </el-form-item>
      <el-form-item
        v-if="passwordVisible"
        :label="$t('message.signIn.password')"
        prop="password"
        :rules="passwordRules"
        :error="formError"
      >
        <el-input
          ref="password"
          v-model="form.password"
          show-password
          :disabled="loading"
          clearable
          :placeholder="$t('message.signIn.passwordPlaceholder')"
          @input="onPasswordInput"
          @keyup.enter="doSignIn"
        />
      </el-form-item>
      <div v-if="actionType === ActionType.Login || actionType === ActionType.RegisterAutoLogin" class="forgot-password-wrapper">
        <div class="text-link" text @click="goForgotPassword">
          {{ $t('message.signIn.forgotPassword') }}?
        </div>
      </div>
      <el-form-item class="btns">
        <el-button
          ref="actionBtn"
          v-track-click
          type="primary"
          round
          class="btn"
          style="width: 100%"
          size="large"
          :loading="loading"
          :name="actionButtonName"
          @click="doSignIn"
        >
          {{ actionButtonText }}
        </el-button>
      </el-form-item>
    </el-form>
    <div v-if="!isVoghion" class="privacy">
      <el-tooltip
        :visible="confirmAcceptTermsVisible"
        effect="dark"
        :content="$t('message.signIn.confirmAcceptPrivacy')"
        placement="left"
      >
        <el-checkbox v-model="acceptTerms">
          {{ $t('message.signIn.termsTips') }}
          <app-link to="/terms-conditions" target="_blank" always-underline>
            {{ $t('message.signIn.terms') }} </app-link
          >.
        </el-checkbox>
      </el-tooltip>
      <el-checkbox v-model="acceptNewsletter">
        {{ $t('message.signIn.newsletterTips') }}
      </el-checkbox>
      <el-tooltip
        popper-class="sign-in-privacy-tooltip"
        :visible="confirmAcceptPrivacyVisible"
        effect="dark"
        :content="$t('message.signIn.confirmAcceptPrivacy')"
        placement="left-start"
      >
        <el-checkbox ref="privacyCheckbox" v-model="acceptPrivacy" class="privacy-checkbox">
          <div>
              {{ $t('message.signIn.privacyTips') }}
              {{ $t('message.signIn.privacy') }}.
          </div>
        </el-checkbox>
        
      </el-tooltip>
    </div>
  </div>
</template>

<script lang="ts" setup>
import {
  emailCheck,
  emailLogin,
  EmailLoginResponse
} from '@/api/user'
import { ElMessage } from 'element-plus'
import { ref, reactive, computed, watch, onMounted, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { usePlatformName, useIsVoghion } from '@/composables/use-req'
import { useUserStore, LoginMethod } from '@/states/user'
import { emailSuggestions } from './helper'
import { Action, PageType } from './constant'

const props = withDefaults(
  defineProps<{
    /** 默认邮箱 */
    defaultEmail?: string
    /** 操作类型 */
    actionType?: ActionType
  }>(),
  {
    defaultEmail: '',
  }
)

const emits = defineEmits<{
  'go-page': [PageType]
  'update:action': [Action]
}>()

enum ActionType {
  Auto = 1, // 检测
  Login = 2, // 登录
  Register = 3, // 注册
  RegisterAutoLogin = 4 // 注册自动登录
}

const { t } = useI18n()
const actionType = ref(props.actionType || ActionType.Auto)
const platform = usePlatformName()
const pageTitle = computed(() => {
  switch (actionType.value) {
    case ActionType.Login:
      return t('message.signIn.main')
    case ActionType.Register:
      return t('message.signIn.registerTitle', {
        platform
      })
    case ActionType.RegisterAutoLogin:
      return t('message.signIn.registerAutoLoginTitle')
    default:
      return `${t('message.signIn.main')} / ${t('message.signIn.register')}`
  }
})
const actionButtonText = computed(() => {
  return actionType.value === ActionType.Register
    ? t('message.signIn.register')
    : t('message.common.continue')
})
const actionButtonName = computed(() => {
  return actionType.value === ActionType.Register ? 'btn_register' : 'btn_login'
})

// const newForm = reactive({
//   email: props.defaultEmail,
//   password: ''
// })
const form = ref({
  email: props.defaultEmail,
  password: ''
})

const emailRules = ref([
  {
    required: true,
    message: t('message.signIn.emailPlaceholder'),
    trigger: ['change', 'blur']
  }
])
const passwordRules = ref([
  {
    required: true,
    message: t('message.signIn.passwordPlaceholder'),
    trigger: ['change', 'blur']
  },
  {
    min: 5,
    max: 16,
    message: t('message.signIn.passwordInvalid')
  }
])
const formError = ref('')
const email = ref(props.defaultEmail)
const password = ref()
const confirmAcceptTermsVisible = ref(false)
const confirmAcceptPrivacyVisible = ref(false)
const acceptPrivacy = ref(true)
const acceptNewsletter = ref(true)
const acceptTerms = ref(true)
const actionBtn = ref()
const privacyCheckbox = ref()
const onEmailChange = () => {
  // if (actionType.value !== ActionType.Auto) {
  //   actionType.value = ActionType.Auto
  // }
  formError.value = ''
}
const passwordVisible = computed(() => {
  return actionType.value !== ActionType.Auto
})
const onPasswordInput = (val) => {
  form.value.password = form.value.password.replace(/\s/gi, '')
  formError.value = ''
}
const userStore = useUserStore()
const loginForm = ref<HTMLFormElement | null>(null)
const loading = ref(false)
const doNext = () => {
  if (actionType.value === ActionType.Auto) {
    doSignIn()
  } else {
    password.value.ref.focus()
  }
}
const checkAcceptPrivacy = () => {
  if (!acceptTerms.value) {
    confirmAcceptTermsVisible.value = true
  } else if (!acceptPrivacy.value) {
    confirmAcceptPrivacyVisible.value = true
  }
  const res = acceptPrivacy.value && acceptTerms.value
  if (!res) {
    privacyCheckbox.value.$el.scrollIntoView()
  }
  return res
}
watch(acceptPrivacy, () => {
  confirmAcceptPrivacyVisible.value = false
})
watch(acceptTerms, () => {
  confirmAcceptTermsVisible.value = false
})

const doSignIn = () => {
  loginForm.value?.validate().then(
    () => {
      if (actionType.value === ActionType.Auto) {
        loading.value = true
        emailCheck({
          userName: form.value.email
        }).then((res) => {
          loading.value = false
          if (res) {
            actionType.value = res.data ? ActionType.Login : ActionType.Register
            nextTick(() => {
              password.value.ref.focus()
            })
          }
        })
      } else if (checkAcceptPrivacy()) {
        if (actionType.value === ActionType.Register) {
          // 手动注册，本次不处理
        } else if (actionType.value === ActionType.Login || actionType.value === ActionType.RegisterAutoLogin) {
          loading.value = true
          formError.value = ''
          emailLogin(
            {
              userName: form.value.email,
              password: form.value.password,
              newsletter: acceptNewsletter.value
            }
          ).then((res) => {
            loading.value = false
            if (!res.success) {
              formError.value = res.rtn_msg
            } else {
              ElMessage.success({
                message: t('message.signIn.success')
              })
              userStore.afterLoginSuccess(res.data)
            }
          }).finally(() => {
            loading.value = false
          })
        }
      }
    },
    () => {}
  )
}
const goForgotPassword = () => {
  emits('go-page', PageType.ForgotPassword)
}


const isVoghion = useIsVoghion()
const getEmail = () => {
  return form.value.email
}

onMounted(() => {
  userStore.loadLoginConfig({})
})

defineExpose({
  getEmail
})
</script>

<style lang="scss">
.sign-in {
  background: white;
  padding: 4px 14px 30px;

  .title {
    margin-top: 0;
    margin-bottom: 24px;
    font-size: 20px;
    text-align: center;
    font-weight: 600;
    &.register-auto-login {
      margin-bottom: 76px;
      font-size: 14px;
      line-height: 20px;
      text-align: left;
      font-weight: 400;
    }
  }

  :deep(.email-input) {
    width: 100%;

    &.email-confirmed {
      .el-input__wrapper {
        background-color: var(--el-disabled-bg-color);
        box-shadow: 0 0 0 1px var(--el-disabled-border-color) inset;
      }
    }
  }

  .forgot-password-wrapper {
    width: 100%;
    display: flex;
    justify-content: flex-end;

    .text-link {
      font-size: 14px;
      line-height: 16px;
      color: $c-black3;
      cursor: pointer;
    }
  }

  .btns {
    margin-top: 24px;

    .el-form-item__content {
      justify-content: center;

      .btn {
        width: 100%;
        font-size: 18px;
        font-weight: 500;
      }
    }
  }

  .title2 {
    margin-top: 54px;
    margin-bottom: 38px;

    :deep(.el-divider__text) {
      font-size: 18px;
      text-align: center;
      color: $c-black2;
      white-space: nowrap;
    }
  }

  .vendor-login {
    display: flex;
    justify-content: space-between;

    .vendor-item {
      flex: 1;
      color: $c-black2;
      font-size: 14px;
      font-weight: bold;

      .icon {
        margin-right: 4px;
        width: 22px;
        height: 22px;
      }

      + .vendor-item {
        margin-left: 10px;
      }
    }
  }

  .privacy {
    margin-top: 22px;
    line-height: 1.5;
    text-align: left;
    color: $c-black2;

    .el-checkbox {
      --el-checkbox-input-border-color-hover: #333;
      --el-checkbox-checked-bg-color: #333;
      --el-checkbox-checked-input-border-color: #333;
      --el-checkbox-border-radius: 50%;
      --el-checkbox-checked-text-color: #606266;
      --el-checkbox-height: auto;
      --el-checkbox-font-size: 12px;

      align-items: flex-start;

      + .el-checkbox {
        margin-top: 8px;
      }
    }

    :deep(.el-checkbox__label) {
      white-space: wrap;
      line-height: 1.2;
    }

    a {
      vertical-align: top;

      --el-link-text-color: #{$c-link1};
      --el-link-hover-text-color: #{$c-link1-hover};
    }
  }
  .privacy-checkbox {
    display: flex;
    width: 100%;
    .el-checkbox__label {
      display: flex;
      flex: 1;
    }
  }
  .el-form-item__label {
    font-weight: 600;
  }
  .el-input__wrapper.is-focus {
    box-shadow: 0 0 0 1px #FF9F00;
  }
}

:global(.sign-in-privacy-tooltip) {
  top: -8px !important;
}

:global(.sign-in-privacy-tooltip .el-popper__arrow) {
  top: -7px !important;
}
</style>
